import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, Button, Form, Spinner, Row, Col, Offcanvas } from 'react-bootstrap';
import { FaUserCircle, FaEnvelope, FaBell, FaPlus, FaTrash, FaUsersCog, FaHistory, FaCheckCircle } from 'react-icons/fa';
import CreatableSelect from 'react-select/creatable';
import Swal from 'sweetalert2';
import { 
    Box, Typography, IconButton, Tooltip, TextField, 
    Checkbox, FormControlLabel, Divider, CircularProgress,
    Avatar
} from '@mui/material';
import { Notifications, Add, Save, Timeline, Email } from '@mui/icons-material';

const ProjectTeam = ({ 
    projectId, 
    projectData, 
    fetchProjectDetails, 
    onAddContact,
    onMinimizeParent,
    onReopenParent 
}) => {
    const [loading, setLoading] = useState(false);
    const [internalContacts, setInternalContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [error, setError] = useState(null);

    // Update Offcanvas state
    const [updateOpen, setUpdateOpen] = useState(false);
    const [updateForm, setUpdateForm] = useState({ description: '', notify_team: true });
    const [updateSaving, setUpdateSaving] = useState(false);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const userInfo = JSON.parse(localStorage.getItem('user-info'));
    const origin = localStorage.getItem('user-origin');
    const token = userInfo?.token;
    const headers = { 
        Authorization: `Bearer ${token}`,
        'Tenant-Origin': origin 
    };

    useEffect(() => {
        fetchInternalContacts();
    }, []);

    const fetchInternalContacts = async () => {
        try {
            const response = await axios.get(`${backendUrl}/api/contact?contact_type=internal`, { 
                headers, skipLoader: true 
            });
            const contacts = (response.data.Data || response.data.data || []);
            const options = contacts.map(c => ({
                value: c.id,
                label: `${c.name} (${c.email})`,
                name: c.name,
                email: c.email,
                __isNew__: false
            }));
            setInternalContacts(options);
        } catch (error) {
            console.error('Error fetching internal contacts:', error);
        }
    };

    const handleAssignMember = async () => {
        if (!selectedContact) return;
        
        const isNew = selectedContact?.__isNew__;
        
        if (isNew) {
            // If it's a new contact typing, open the standard contact modal with the name pre-filled
            if (onAddContact) {
                // We'll pass the name as part of the "preset" if the modal supports it
                // For now, just trigger the modal opening which is what the user asked
                onAddContact();
                setSelectedContact(null);
            }
            return;
        }

        setLoading(true);
        try {
            const contactId = selectedContact.value;
            const existingTeam = projectData?.project_team || (projectData?.contacts || []).filter(c => c.contact_type === 'internal');
            
            if (existingTeam.some(c => c.id === contactId)) {
                Swal.fire('Warning', 'Member is already assigned to the team.', 'warning');
                setLoading(false);
                return;
            }

            const payload = {
                contact_id: contactId,
                send_assignment_email: true,
                role: 'Team Member'
            };

            await axios.post(`${backendUrl}/api/project-team/${projectId}`, payload, { headers });

            Swal.fire({ icon: 'success', title: 'Member Assigned & Emailed', timer: 2000, showConfirmButton: false });
            setSelectedContact(null);
            if (fetchProjectDetails) fetchProjectDetails();
        } catch (error) {
            Swal.fire('Error', 'Failed to update team.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePostUpdate = async () => {
        if (!updateForm.description.trim()) {
            Swal.fire('Validation', 'Please enter update description.', 'warning');
            return;
        }
        setUpdateSaving(true);
        try {
            // Posting to project_updates API
            await axios.post(`${backendUrl}/api/projects/${projectId}/updates`, updateForm, { 
                headers: { Authorization: `Bearer ${token}` }
            });
            Swal.fire({ icon: 'success', title: 'Update Logged!', timer: 1800, showConfirmButton: false });
            setUpdateOpen(false);
            if (onReopenParent) onReopenParent();
            setUpdateForm({ description: '', notify_team: true });
        } catch (e) {
            Swal.fire('Error', 'Failed to log update.', 'error');
        } finally {
            setUpdateSaving(false);
        }
    };

    const toggleNotification = async (contactId, field) => {
        try {
            const currentTeam = projectData?.project_team || (projectData?.contacts || []).filter(c => c.contact_type === 'internal');
            const updatedTeam = currentTeam.map(contact => {
                const isTarget = contact.id === contactId;
                const pivot = contact.pivot || {};
                return {
                    id: contact.id,
                    sendemail_to_project: isTarget && field === 'sendemail_to_project' ? (pivot.sendemail_to_project === 'true' ? 'false' : 'true') : (pivot.sendemail_to_project || 'false'),
                    receive_updates: isTarget && field === 'receive_updates' ? (pivot.receive_updates === 'true' ? 'false' : 'true') : (pivot.receive_updates || 'false')
                };
            });

            await axios.post(`${backendUrl}/api/projects/${projectId}?_method=PUT`, 
                { name: projectData.name, project_team: updatedTeam }, { headers }
            );
            if (fetchProjectDetails) fetchProjectDetails();
        } catch (error) {
            console.error(error);
        }
    };

    const removeMember = async (contactId) => {
        const result = await Swal.fire({
            title: 'Remove from Team?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, remove'
        });
        if (result.isConfirmed) {
            const currentTeam = projectData?.project_team || (projectData?.contacts || []).filter(c => c.contact_type === 'internal');
            const updatedTeam = currentTeam.filter(c => c.id !== contactId).map(c => ({
                id: c.id,
                sendemail_to_project: c.pivot?.sendemail_to_project || 'false',
                receive_updates: c.pivot?.receive_updates || 'false'
            }));
            await axios.post(`${backendUrl}/api/projects/${projectId}?_method=PUT`, 
                { name: projectData.name, project_team: updatedTeam }, { headers }
            );
            if (fetchProjectDetails) fetchProjectDetails();
        }
    };

    const teamList = projectData?.project_team || (projectData?.contacts || []).filter(c => c.contact_type === 'internal');

    return (
        <div className="project-team-tab p-4 bg-light rounded shadow-sm">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <h5 className="mb-0 text-primary d-flex align-items-center fw-bold">
                    <FaUsersCog className="me-2" /> Project Team (Internal)
                </h5>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Post Project Activity Update">
                        <IconButton 
                            size="small" 
                            onClick={() => {
                                if (onMinimizeParent) onMinimizeParent();
                                setUpdateOpen(true);
                            }}
                            sx={{ bgcolor: '#1a73e8', color: 'white', '&:hover': { bgcolor: '#1557b0' } }}
                        >
                            <Notifications />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Row>
                <Col md={8}>
                    <Card className="border-0 shadow-sm overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-sales-gradient text-uppercase small fw-bold">
                                        <tr>
                                            <th>Staff Member</th>
                                            <th className="text-end">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {teamList.map(member => (
                                            <tr key={member.id}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Avatar sx={{ width: 34, height: 34, fontSize: 13, mr: 2, bgcolor: '#e8f0fe', color: '#1a73e8', fontWeight: 700 }}>
                                                            {member.name?.charAt(0)}
                                                        </Avatar>
                                                        <div>
                                                            <div className="fw-bold mb-0 text-dark" style={{ fontSize: '13.5px' }}>{member.name}</div>
                                                            <small className="text-muted" style={{ fontSize: '11.5px' }}>{member.email}</small>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="text-end">
                                                    <IconButton size="small" color="error" onClick={() => removeMember(member.id)} sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                                                        <FaTrash size={14} />
                                                    </IconButton>
                                                </td>
                                            </tr>
                                        ))}
                                        {teamList.length === 0 && (
                                            <tr>
                                                <td colSpan="3" className="text-center py-5 text-muted small italic">No team members linked to this project.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={4}>
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white py-3 border-bottom text-center">
                            <h6 className="mb-0 fw-bold small text-uppercase text-muted">Link Team Member</h6>
                        </Card.Header>
                        <Card.Body>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Typography variant="caption" fontWeight={600} color="text.secondary">Select or Type Member</Typography>
                                <CreatableSelect
                                    isClearable
                                    options={internalContacts}
                                    value={selectedContact}
                                    onChange={setSelectedContact}
                                    placeholder="Search or type name..."
                                    formatCreateLabel={(inputValue) => `+ Create "${inputValue}" as Internal Member`}
                                    allowCreateWhileLoading={true}
                                    createOptionPosition="first"
                                    styles={{
                                        control: (base) => ({ ...base, fontSize: '13px', borderRadius: '6px' }),
                                        option: (base) => ({ ...base, fontSize: '13px' }),
                                    }}
                                />

                                <Button 
                                    variant="primary" 
                                    className="w-100 py-2 d-flex align-items-center justify-content-center shadow-sm" 
                                    onClick={handleAssignMember} 
                                    disabled={loading || !selectedContact}
                                    style={{ borderRadius: '6px', fontWeight: 600, fontSize: '13px' }}
                                >
                                    {loading ? <Spinner size="sm" className="me-2" /> : <Add className="me-1" fontSize="small" />}
                                    Link Staff Member
                                </Button>
                            </Box>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* PROJECT UPDATE SIDE OFFCANVAS */}
            <Offcanvas
                show={updateOpen}
                onHide={() => {
                    if (!updateSaving) {
                        setUpdateOpen(false);
                        if (onReopenParent) onReopenParent();
                    }
                }}
                placement="end"
                style={{ width: '450px' }}
            >
                <Offcanvas.Header closeButton className="border-bottom bg-light">
                    <Offcanvas.Title className="fw-bold fs-6">
                        <FaHistory className="me-2 text-primary" /> Post Project Activity Update
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                        <TextField
                            label="What's the update?"
                            multiline
                            rows={8}
                            fullWidth
                            variant="outlined"
                            placeholder="Type progress details or comments here..."
                            value={updateForm.description}
                            onChange={e => setUpdateForm({ ...updateForm, description: e.target.value })}
                        />

                        <Box sx={{ p: 2, bgcolor: '#f0f4ff', borderRadius: 2, border: '1px solid #d0deff' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={updateForm.notify_team} 
                                        onChange={e => setUpdateForm({ ...updateForm, notify_team: e.target.checked })} 
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <FaBell className="text-primary me-2" />
                                        <Typography variant="body2" fontWeight={700}>Email Project Updates to Team</Typography>
                                    </Box>
                                }
                            />
                            <Typography variant="caption" display="block" sx={{ ml: 4, color: 'text.secondary' }}>
                                This will notify all assigned team members who have enabled project notifications.
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Button variant="secondary" className="w-50 py-2" onClick={() => setUpdateOpen(false)}>Cancel</Button>
                            <Button 
                                variant="primary" 
                                className="w-50 py-2 fw-bold" 
                                onClick={handlePostUpdate} 
                                disabled={updateSaving}
                            >
                                {updateSaving ? <Spinner size="sm" className="me-2" /> : <FaCheckCircle className="me-2" />}
                                Post Update
                            </Button>
                        </Box>
                    </Box>
                </Offcanvas.Body>
            </Offcanvas>
        </div>
    );
};

export default ProjectTeam;
